import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import NotificationAPI from "../services/NotificationAPI";
import { useUser } from "./UserContext";

/**
 * Notification Context with REST API Polling
 *
 * Features:
 * - Real-time notifications via WebSocket
 * - Automatic connection management
 * - Fallback to REST API for initial load
 * - Browser notification support
 */
const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { loggedInUser } = useUser();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastLoadTimeRef = useRef(0);
  const loadingRef = useRef(false);
  const wsUnsubscribeRef = useRef(null);
  const wsConnectionUnsubscribeRef = useRef(null);

  /**
   * Handle incoming WebSocket notification
   * Adds to state and updates unread count
   */
  const handleWebSocketNotification = useCallback((notification) => {
    // Add new notification to the beginning of the list
    setNotifications((prev) => {
      // Check if notification already exists (prevent duplicates)
      const exists = prev.some(
        (n) => n.notificationId === notification.notificationId,
      );
      if (exists) {
        return prev;
      }
      return [notification, ...prev];
    });

    // Increment unread count if notification is unread
    if (!notification.isRead) {
      setUnreadCount((prev) => prev + 1);
    }
  }, []);

  const loadNotificationCounts = useCallback(async () => {
    if (!loggedInUser?.uuid) return;

    try {
      const counts = await NotificationAPI.getNotificationCounts(
        loggedInUser.uuid,
      );
      console.log(
        "NotificationContext: Loaded notification counts:",
        JSON.stringify(counts, null, 2),
      );
      console.log("NotificationContext: unreadCount:", counts.unreadCount);
      console.log("NotificationContext: badgeCount:", counts.badgeCount);
      console.log(
        "NotificationContext: emailAnalysisCount:",
        counts.emailAnalysisCount,
      );
      console.log(
        "NotificationContext: Setting unreadCount to:",
        counts.badgeCount || 0,
      );
      setUnreadCount(counts.badgeCount || 0);
    } catch (err) {
      console.error("Failed to load notification counts:", err);
      setError("Failed to load notification counts");
    }
  }, [loggedInUser]);

  // Initialize WebSocket connection when user logs in
  useEffect(() => {
    if (loggedInUser?.uuid) {
      // Initial load from REST API
      loadNotificationCounts();
    } else {
      // Reset state when user logs out
      setUnreadCount(0);
      setNotifications([]);
      setError(null);
    }
  }, [loggedInUser, loadNotificationCounts, handleWebSocketNotification]);

  const loadNotifications = async () => {
    if (!loggedInUser?.uuid) return;

    const now = Date.now();

    // Prevent multiple simultaneous calls
    if (loadingRef.current) {
      console.log(
        "NotificationContext: Already loading notifications, skipping...",
      );
      return;
    }

    // Throttle calls - don't load if called within last 1 second
    if (now - lastLoadTimeRef.current < 1000) {
      console.log("NotificationContext: Throttling loadNotifications call");
      return;
    }

    loadingRef.current = true;
    lastLoadTimeRef.current = now;
    setLoading(true);
    setError(null);

    try {
      console.log("NotificationContext: Loading notifications...");
      const notificationData = await NotificationAPI.getUserNotifications(
        loggedInUser.uuid,
      );
      // Ensure we always set an array, even if API returns something unexpected
      const safeNotificationData = Array.isArray(notificationData)
        ? notificationData
        : [];
      setNotifications(safeNotificationData);
      // Sync badge count with the freshly loaded list so the bell icon
      // reflects any new notifications created since the last count load
      // (e.g. forwarded-email analyses that arrive asynchronously).
      await loadNotificationCounts();
    } catch (err) {
      console.error("Failed to load notifications:", err);
      setError("Failed to load notifications");
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const markAsRead = async (notificationIds) => {
    if (!loggedInUser?.uuid) return;

    try {
      await NotificationAPI.markAsRead(loggedInUser.uuid, notificationIds);

      // Optimistically update local state to prevent flashing
      setNotifications((prev) =>
        Array.isArray(prev)
          ? prev.map((notification) =>
              notificationIds.includes(notification.notificationId)
                ? { ...notification, isRead: true }
                : notification,
            )
          : [],
      );
      setUnreadCount((prev) => Math.max(0, prev - notificationIds.length));

      // Debounce the API refresh to prevent multiple rapid calls
      setTimeout(() => {
        loadNotificationCounts();
      }, 500);
    } catch (err) {
      console.error("Failed to mark notifications as read:", err);
      setError("Failed to mark notifications as read");
      // Revert optimistic updates on error
      await loadNotifications();
    }
  };

  const markAllAsRead = async () => {
    if (!loggedInUser?.uuid) return;

    try {
      await NotificationAPI.markAllAsRead(loggedInUser.uuid);

      // Optimistically update all notifications to read
      setNotifications((prev) =>
        Array.isArray(prev)
          ? prev.map((notification) => ({ ...notification, isRead: true }))
          : [],
      );
      setUnreadCount(0);

      // Minimal refresh after a delay
      setTimeout(() => {
        loadNotificationCounts();
      }, 1000);
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
      setError("Failed to mark all notifications as read");
      // Revert on error
      await loadNotifications();
    }
  };

  const dismissNotifications = async (notificationIds) => {
    if (!loggedInUser?.uuid) return;

    try {
      await NotificationAPI.dismissNotifications(
        loggedInUser.uuid,
        notificationIds,
      );

      // Optimistically remove dismissed notifications
      setNotifications((prev) =>
        Array.isArray(prev)
          ? prev.filter(
              (notification) =>
                !notificationIds.includes(notification.notificationId),
            )
          : [],
      );

      // Update unread count if dismissed notifications were unread
      const safeNotifications = Array.isArray(notifications)
        ? notifications
        : [];
      const dismissedUnreadCount = safeNotifications.filter(
        (n) => notificationIds.includes(n.notificationId) && !n.isRead,
      ).length;
      setUnreadCount((prev) => Math.max(0, prev - dismissedUnreadCount));

      // Minimal refresh after a delay
      setTimeout(() => {
        loadNotificationCounts();
      }, 1000);
    } catch (err) {
      console.error("Failed to dismiss notifications:", err);
      setError("Failed to dismiss notifications");
      // Revert on error
      await loadNotifications();
    }
  };

  const value = {
    // State
    unreadCount,
    notifications,
    loading,
    error,

    // Actions
    loadNotifications,
    loadNotificationCounts,
    markAsRead,
    markAllAsRead,
    dismissNotifications,

    // Utilities
    setError,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
